import React, {ChangeEvent} from 'react';
import RedirectPageSelect from './RedirectPageSelect/RedirectPageSelect';
import {
  HStack,
  SegmentedControl,
  SegmentedControlButton,
  Text,
  TextInput,
  VStack,
  View,
  PageDefaultIcon,
  IconComponent,
} from '@packages/systems/spring';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {REDIRECT_PATH} from '../constants';
import debounce from 'lodash/debounce';
import {
  PageListNodeType,
  PageRecordType,
} from '@packages/systems/core/records/PageRecord';
import * as PageManagerUtils from '@packages/shared/utils/PageManagerUtils';
import {
  getPageManagerUIStore,
  getPageStore,
} from '@packages/systems/designer/page';
import {fullSlug, getPageTreeNodes} from '@packages/shared/utils/PageTreeUtils';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import type {Locale} from '@packages/systems/localization-types';
import {
  selectCurrentLocale,
  selectIsAllowedToDraftPagesPerLocale,
} from '@packages/systems/localization';
import {selectCurrentPage} from '@packages/utilities/platform-selectors';

const getCurrentLocale: (
  state: DesignerStores
) => Locale | null | undefined = ({LocalizationStore}) =>
  selectCurrentLocale(LocalizationStore);

enum RedirectType {
  None = 'None',
  Page = 'Page',
  URL = 'URL',
}

const LABEL_WIDTH = '20%';

type RedirectSectionProps = {
  expr: ContextualExpression;
  updateRedirect: (url: string) => void;
};

export type SearchableListPage = {
  label: string;
  value: string;
  prefix?: IconComponent;
  url?: string;
};

const formatPagesForList = (
  staticPages: PageListNodeType[],
  currentPage?: PageRecordType
): SearchableListPage[] => {
  const staticPagesOptions: SearchableListPage[] = [];

  staticPages.forEach((page: PageListNodeType) => {
    if (page.id !== currentPage?.id && page.type !== 'Folder') {
      staticPagesOptions.push({
        label: page.name,
        value: page.id,
        prefix: PageDefaultIcon,
      });
    }
  });

  return staticPagesOptions;
};

const RedirectSection = ({expr, updateRedirect}: RedirectSectionProps) => {
  // The selected page in the page select
  const [pageValue, setPageValue] = React.useState<{
    page?: PageListNodeType;
    url?: string;
  }>();
  // The URL in the custom url text input
  const [customValue, setCustomValue] = React.useState<string>('');
  // The selected redirect type
  const [redirectType, setRedirectType] = React.useState<RedirectType>(
    RedirectType.None
  );

  // Page Select logic
  // lives here because we need to know if we have a page on the first render
  const pageState = useDesignerSelector(getPageStore);
  const pageManagerState = useDesignerSelector(getPageManagerUIStore);
  const nodes = getPageTreeNodes(pageState);
  const currentLocale = useDesignerSelector(getCurrentLocale);
  const currentPage = useDesignerSelector(selectCurrentPage);
  const isAllowedToDraftPagesPerLocale = useDesignerSelector(
    selectIsAllowedToDraftPagesPerLocale
  );

  const staticPages: PageListNodeType[] = React.useMemo(
    () =>
      PageManagerUtils.staticPageList(
        pageState,
        pageManagerState,
        currentLocale,
        isAllowedToDraftPagesPerLocale,
        {
          skipUtilityPages: true,
          skipNestedInFolder: false,
          includeUsysUtilityPages: false,
          skipBranchPages: false,
          skipStandaloneBranchPageParents: false,
          skipStaticTemplates: false,
        }
      ),
    [pageState, pageManagerState, currentLocale, isAllowedToDraftPagesPerLocale]
  );

  const staticPagesOptions = React.useMemo(
    () => formatPagesForList(staticPages, currentPage),
    [staticPages, currentPage]
  );

  // Updates the WFDL expression with the new redirect url
  const debouncedSetRedirectUrl = React.useCallback(
    // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
    debounce((url: string) => {
      updateRedirect(url);
    }, 500),
    [updateRedirect]
  );

  // updates the custom url input and the WFDL expression
  const onCustomUrlChange = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const url = e.target.value;
      setCustomValue(url);
      debouncedSetRedirectUrl(url);
    },
    [debouncedSetRedirectUrl, setCustomValue]
  );

  const onPageSelect = React.useCallback(
    (selectedPageId: string) => {
      const staticPage = staticPages.find((page) => page.id === selectedPageId);
      const staticUrl = `/${fullSlug(selectedPageId, nodes)}`;
      setPageValue({page: staticPage, url: staticUrl});
      debouncedSetRedirectUrl(staticUrl);
    },
    [setPageValue, debouncedSetRedirectUrl, nodes, staticPages]
  );

  // First render logic only.
  // This effect should not make any updates to the expression
  React.useEffect(() => {
    const {getIn} = ContextualExpressions;
    const expression = getIn(expr, REDIRECT_PATH);
    // @ts-expect-error TS2339: Property expr does not exist on type
    const url = (expression && stripAnn(expression).val) || '';

    // Checks the URL against static pages to see if a page is already selected as the redirect
    // Must run before checking against type None or URL because it needs to set the PageListNode for the home page
    let staticUrl;
    let staticPage;
    for (const page of staticPages) {
      staticUrl = `/${fullSlug(page.id, nodes)}`;
      if (page.isHome) {
        setPageValue({page, url: staticUrl});
      }
      if (staticUrl === url) {
        setRedirectType(RedirectType.Page);
        staticPage = page;
        setPageValue({page, url});
      }
    }

    // If the URL is not a static page, then set the redirect type to either None or URL
    if (staticPage) {
      return;
    } else if (!url) {
      setRedirectType(RedirectType.None);
    } else {
      setRedirectType(RedirectType.URL);
      setCustomValue(url);
    }

    // No dependencies as we only want this to run on first render
  }, []);

  // Switches the type of redirect and updates the expression with the stored state
  const switchTypeAndUpdateExpr = React.useCallback(
    (type: RedirectType) => {
      // Do nothing if already on redirect type
      if (type === redirectType) {
        return;
      }
      if (type === RedirectType.Page) {
        updateRedirect(pageValue?.url || '');
      } else if (type === RedirectType.URL) {
        updateRedirect(customValue);
      } else {
        updateRedirect('');
      }

      setRedirectType(type);
    },
    [redirectType, updateRedirect, customValue, pageValue]
  );

  return (
    <VStack>
      <HStack>
        <View width={LABEL_WIDTH}>
          <Text colorVariant="secondary">Redirect</Text>
        </View>
        <SegmentedControl
          keyboardActivation="manual"
          onChange={(option) => switchTypeAndUpdateExpr(option as RedirectType)}
          value={redirectType}
        >
          <SegmentedControlButton
            data-automation-id="redirect-type-switch-none"
            value={RedirectType.None}
          >
            {RedirectType.None}
          </SegmentedControlButton>
          <SegmentedControlButton
            data-automation-id="redirect-type-switch-page"
            value={RedirectType.Page}
          >
            {RedirectType.Page}
          </SegmentedControlButton>
          <SegmentedControlButton
            data-automation-id="redirect-type-switch-url"
            value={RedirectType.URL}
          >
            {RedirectType.URL}
          </SegmentedControlButton>
        </SegmentedControl>
      </HStack>
      {redirectType !== RedirectType.None && (
        <HStack data-automation-id="redirect-by-type-input">
          <View width={LABEL_WIDTH}>
            <Text colorVariant="secondary">{redirectType}</Text>
          </View>
          {redirectType === RedirectType.Page && (
            <View width="80%">
              {pageValue && (
                <RedirectPageSelect
                  selectedPage={pageValue}
                  onPageSelect={onPageSelect}
                  staticPagesOptions={staticPagesOptions}
                />
              )}
            </View>
          )}
          {redirectType === RedirectType.URL && (
            <TextInput
              data-automation-id="redirect-url-input"
              value={customValue}
              placeholder="e.g. /success"
              onChange={onCustomUrlChange}
            />
          )}
        </HStack>
      )}
    </VStack>
  );
};

export default RedirectSection;
